import React from 'react';
import { FieldProps } from '@rjsf/core';
import { useIntl } from 'lib/intl';
import { Box, Flex } from 'components/layout';
import { Text } from 'components/typography';
import { TextField } from 'components/form-elements/Forms/FieldEditText';
import { Select } from 'components/form-elements/Forms/SelectField';

/**
 * "Layers" section for the Spray installation type.
 *
 * Renders three optional layer entries (Layer 1/2/3). Each entry has an
 * integer-only `number` input and a `time` value captured as two dropdowns
 * (hours 0-99, minutes in 5-minute steps). Layout is inline on desktop
 * (label + number + hours : minutes) and stacks the label above the inputs on
 * mobile. Nothing is required.
 *
 * Registered as the custom `sprayLayers` field and gated to the Spray
 * installation type via the ui-schema dependents.
 */

const LAYER_KEYS = ['layer1', 'layer2', 'layer3'] as const;
type LayerKey = (typeof LAYER_KEYS)[number];

const HOURS = Array.from({ length: 100 }, (_, i) => i); // 0..99
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,..,55

type LayerTime = { hours?: number; minutes?: number };
type Layer = { number?: number; time?: LayerTime };
type LayersData = Partial<Record<LayerKey, Layer>>;

export function SprayLayers(props: FieldProps<LayersData>) {
  const intl = useIntl();
  const data: LayersData = props.formData ?? {};
  const { disabled } = props;
  const baseId = props.idSchema?.$id ?? 'layers';

  function updateLayer(key: LayerKey, patch: Partial<Layer>) {
    props.onChange({
      ...data,
      [key]: { ...data[key], ...patch },
    });
  }

  function onNumberChange(key: LayerKey, raw: string) {
    // Integer only - strip everything that is not a digit (no decimals, no sign).
    const digits = raw.replace(/\D/g, '');
    updateLayer(key, { number: digits === '' ? undefined : Number(digits) });
  }

  function onTimeChange(key: LayerKey, part: keyof LayerTime, raw: string) {
    const time = data[key]?.time ?? {};
    updateLayer(key, {
      time: { ...time, [part]: raw === '' ? undefined : Number(raw) },
    });
  }

  return (
    <Box>
      <Text
        as="h2"
        sx={{ fontWeight: 300, fontSize: '3xl', mb: 5 }}
        intlId="relining.layers.title"
      />

      <Flex sx={{ flexDirection: 'column', gap: 6 }}>
        {LAYER_KEYS.map((key) => {
          const layer = data[key] ?? {};
          const time = layer.time ?? {};

          return (
            <Flex
              key={key}
              sx={{
                flexDirection: ['column', 'row'],
                alignItems: ['stretch', 'center'],
                gap: [2, 4],
              }}
            >
              <Text
                as="p"
                sx={{ fontWeight: 500, flexShrink: 0, minWidth: [null, '72px'] }}
              >
                {intl.formatMessage({ id: `relining.layers.${key}` })}
              </Text>

              <Flex sx={{ flex: 1, gap: 3, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    name={`${baseId}_${key}_number`}
                    label={intl.formatMessage({ id: 'relining.layers.number' })}
                    value={layer.number != null ? String(layer.number) : ''}
                    isError={false}
                    type="text"
                    inputMode="numeric"
                    disabled={disabled}
                    onBlur={() => undefined}
                    onChange={(event) =>
                      onNumberChange(
                        key,
                        (event.target as HTMLInputElement).value,
                      )}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Select
                    name={`${baseId}_${key}_hours`}
                    label={intl.formatMessage({ id: 'relining.layers.hours' })}
                    value={time.hours != null ? String(time.hours) : ''}
                    isError={false}
                    disabled={disabled}
                    onBlur={() => undefined}
                    onChange={(event) =>
                      onTimeChange(
                        key,
                        'hours',
                        (event.target as HTMLSelectElement).value,
                      )}
                  >
                    <option value="" aria-label="empty" />
                    {HOURS.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Text as="span" sx={{ flexShrink: 0, color: 'darkgrey.600' }}>
                  :
                </Text>

                <Box sx={{ flex: 1 }}>
                  <Select
                    name={`${baseId}_${key}_minutes`}
                    label={intl.formatMessage({ id: 'relining.layers.minutes' })}
                    value={time.minutes != null ? String(time.minutes) : ''}
                    isError={false}
                    disabled={disabled}
                    onBlur={() => undefined}
                    onChange={(event) =>
                      onTimeChange(
                        key,
                        'minutes',
                        (event.target as HTMLSelectElement).value,
                      )}
                  >
                    <option value="" aria-label="empty" />
                    {MINUTES.map((minute) => (
                      <option key={minute} value={minute}>
                        {String(minute).padStart(2, '0')}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}

